import type { Experiment } from 'types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Edit2 } from 'lucide-react';
import { useForm } from 'hooks/useForm';
import { useHypothesis } from '@/state/experiments/useHypothesis';
import { PREDEFINED_MECHANISMS } from '@/state/experiments/expected-mechanisms';
import { useSimilarTests } from '@/state/learnings/useSimilarTests';
import { SimilarPastTests } from './SimilarPastTests';
import { SectionAuthor } from '../SectionAuthor';

type HypothesisProps = { experiment: Experiment };

export const Hypothesis = ({ experiment }: HypothesisProps) => {
  const {
    isEditing,
    startEditing,
    stopEditing,
    saveHypothesis,
    selected,
    custom,
    hasStoredMechanisms,
    showCustom,
    toggleMechanism,
    toggleCustom,
    setCustom,
    commitCustom,
  } = useHypothesis(experiment);

  // For a Draft on a website with learnings on; otherwise it never asks and shows nothing.
  const similar = useSimilarTests({ experiment });

  const {
    formRef,
    state: formState,
    update,
  } = useForm<{ hypothesis: string }>({
    hypothesis: experiment.hypothesis ?? '',
  });

  const onSubmitHypothesis = async (ev: React.FormEvent) => {
    ev.preventDefault();
    // useForm's state is partial: an untouched textarea leaves the field undefined.
    await saveHypothesis(formState.hypothesis ?? '');
  };

  const onCancelHypothesis = () => {
    stopEditing();
    update({ hypothesis: experiment.hypothesis ?? '' });
  };

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="brief-hypothesis">
      <div className="border-line flex items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="micro-label text-foreground">Hypothesis</h2>
          <p className="supporting mt-1">Define the expected outcome and reasoning behind your experiment.</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <SectionAuthor experiment={experiment} section="hypothesis" hasContent={!!experiment.hypothesis?.trim()} />
          {!isEditing && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Edit hypothesis"
              onClick={startEditing}
              className="text-dim hover:text-foreground hover:bg-hover size-8 shrink-0 rounded-lg"
            >
              <Edit2 className="size-3.5" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 p-4">
        {isEditing ? (
          <form ref={formRef} onSubmit={onSubmitHypothesis} className="flex flex-col gap-3">
            <Textarea
              name="hypothesis"
              defaultValue={experiment.hypothesis || ''}
              rows={4}
              placeholder="Write your hypothesis..."
              onChange={(ev) => similar.setText(ev.target.value)}
              className="border-line bg-field text-foreground rounded-lg text-body"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                type="button"
                onClick={onCancelHypothesis}
                className="border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-cta hover:bg-ctahover h-8 rounded-lg px-3 text-meta text-white">
                Save
              </Button>
            </div>
          </form>
        ) : (
          <div className="min-h-[60px]">
            {experiment.hypothesis ? (
              <p className="text-foreground text-body whitespace-pre-wrap">{experiment.hypothesis}</p>
            ) : (
              <p className="supporting">No hypothesis added yet.</p>
            )}
          </div>
        )}

        <SimilarPastTests matches={similar.matches} />

        <div className="border-line flex flex-col gap-2 border-t pt-4" data-tour="brief-mechanisms">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Label className="micro-label text-foreground">Expected mechanisms</Label>
              <p className="supporting mt-1">Clarify what the experiment solves by selecting expected mechanisms.</p>
            </div>
            <SectionAuthor experiment={experiment} section="mechanisms" hasContent={hasStoredMechanisms} />
          </div>

          <TooltipProvider>
            <div className="flex flex-col gap-1">
              {PREDEFINED_MECHANISMS.map((mechanism) => {
                const isSelected = selected.includes(mechanism.id);
                return (
                  <Tooltip key={mechanism.id}>
                    <div className="hover:bg-hover rounded-card flex items-center gap-2 px-2 py-1.5">
                      <Checkbox
                        id={mechanism.id}
                        checked={isSelected}
                        onCheckedChange={(checked) => toggleMechanism(mechanism.id, !!checked)}
                        className="border-line"
                      />
                      <TooltipTrigger asChild>
                        <Label htmlFor={mechanism.id} className="text-foreground cursor-pointer text-meta font-normal">
                          {mechanism.label}
                        </Label>
                      </TooltipTrigger>
                    </div>
                    <TooltipContent className="border-line bg-panel text-foreground rounded-lg">
                      <p className="supporting max-w-xs">{mechanism.description}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>

          <div className="flex items-start gap-2 px-2 py-1.5">
            <Checkbox
              id="other"
              checked={showCustom}
              onCheckedChange={(checked) => toggleCustom(!!checked)}
              className="border-line mt-0.5"
            />
            <div className="flex flex-1 flex-col gap-2">
              <Label htmlFor="other" className="text-foreground cursor-pointer text-meta font-normal">
                Other
              </Label>
              {showCustom && (
                <Input
                  placeholder="Enter custom mechanism"
                  value={custom}
                  onChange={(ev) => setCustom(ev.target.value)}
                  onBlur={commitCustom}
                  className="border-line bg-field text-foreground rounded-lg text-meta"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
